import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Eye, BookOpen, ClipboardCheck, Video, Upload, ChevronDown, ChevronUp, X, FileText, Sparkles } from 'lucide-react';
import { lmsApi, assessmentApi, submissionApi, uploadApi, techniqueApi } from '../services/api';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';

function ManageLMS() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [activeTab, setActiveTab] = useState('courses');

  const tabs = [
    { id: 'courses', label: 'Courses', icon: BookOpen },
    { id: 'assessments', label: 'Assessments', icon: ClipboardCheck },
    { id: 'live-sessions', label: 'Live Sessions', icon: Video },
    { id: 'submissions', label: 'Submissions', icon: Upload },
  ];

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Manage LMS</h1>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <div className="flex space-x-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center pb-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.id
                  ? 'border-primary-600 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <tab.icon className="w-4 h-4 mr-1.5" />
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {activeTab === 'courses' && <CoursesTab />}
      {activeTab === 'assessments' && <AssessmentsTab />}
      {activeTab === 'live-sessions' && <LiveSessionsTab />}
      {activeTab === 'submissions' && <SubmissionsTab />}
    </div>
  );
}

// ========================
// COURSES TAB
// ========================
function CoursesTab() {
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [expandedCourse, setExpandedCourse] = useState(null);
  const [showLessonModal, setShowLessonModal] = useState(false);
  const [editingLesson, setEditingLesson] = useState(null);
  const [lessonCourseId, setLessonCourseId] = useState(null);

  // Course form
  const [form, setForm] = useState({ title: '', description: '', thumbnail_url: '', levels: ['L1', 'L2'], is_published: true, sort_order: 0 });

  // Lesson form
  const [lessonForm, setLessonForm] = useState({ title: '', description: '', youtube_url: '', duration_seconds: 0, levels: ['L1', 'L2'], is_published: true, sort_order: 0 });
  // Lesson can be attached to EITHER a course or a technique. Radio decides which; the
  // opposite dropdown is hidden. lessonCourseId is set implicitly when opening from a course.
  const [lessonTarget, setLessonTarget] = useState('course'); // 'course' | 'technique'
  const [lessonTechniqueSlug, setLessonTechniqueSlug] = useState('');
  const [techniques, setTechniques] = useState([]);

  useEffect(() => { loadCourses(); loadTechniquesList(); }, []);

  const loadTechniquesList = async () => {
    try {
      const r = await techniqueApi.getAllTechniques();
      setTechniques(Array.isArray(r.data) ? r.data : (r.data?.techniques || []));
    } catch (_) { setTechniques([]); }
  };

  const loadCourses = async () => {
    try {
      const res = await lmsApi.getCourses();
      setCourses(res.data);
    } catch (error) {
      toast.error('Failed to load courses');
    } finally { setIsLoading(false); }
  };

  const handleSaveCourse = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    try {
      if (editingCourse) {
        await lmsApi.updateCourse(editingCourse.id, form);
        toast.success('Course updated');
      } else {
        await lmsApi.createCourse(form);
        toast.success('Course created');
      }
      setShowModal(false);
      setEditingCourse(null);
      loadCourses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save course');
    }
  };

  const handleDeleteCourse = async (id) => {
    if (!confirm('Delete this course?')) return;
    try {
      await lmsApi.deleteCourse(id);
      toast.success('Course deleted');
      loadCourses();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  // Swap sort_order of two adjacent items in a list (courses OR lessons).
  // Uses the existing update endpoints — no new backend needed.
  const swapOrder = async (list, index, direction, updateFn) => {
    const target = direction === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= list.length) return;
    const a = list[index];
    const b = list[target];
    // If both have same sort_order (default 0), assign fresh sequence first
    const aOrder = a.sort_order ?? 0;
    const bOrder = b.sort_order ?? 0;
    const newA = bOrder === aOrder ? (direction === 'up' ? aOrder - 1 : aOrder + 1) : bOrder;
    try {
      await Promise.all([
        updateFn(a.id, { sort_order: newA }),
        updateFn(b.id, { sort_order: aOrder }),
      ]);
      loadCourses();
    } catch (_) {
      toast.error('Failed to reorder');
    }
  };

  const moveCourse = (course, index, direction) =>
    swapOrder(courses, index, direction, (id, patch) => lmsApi.updateCourse(id, patch));

  const moveLesson = (lessons, lesson, index, direction) =>
    swapOrder(lessons, index, direction, (id, patch) => lmsApi.updateLesson(id, patch));

  const openEditCourse = (course) => {
    setEditingCourse(course);
    setForm({
      title: course.title,
      description: course.description || '',
      thumbnail_url: course.thumbnail_url || '',
      levels: course.levels || ['L1', 'L2'],
      is_published: course.is_published,
      sort_order: course.sort_order || 0
    });
    setShowModal(true);
  };

  const openNewCourse = () => {
    setEditingCourse(null);
    setForm({ title: '', description: '', thumbnail_url: '', levels: ['L1', 'L2'], is_published: true, sort_order: 0 });
    setShowModal(true);
  };

  // Inline resource management inside the lesson editor. Resources are queued
  // locally (add/delete) and flushed to the server when the admin clicks Save,
  // so a new-lesson flow can add resources before the lesson has a real ID.
  const [lessonResources, setLessonResources] = useState([]);
  const [resourcesToDelete, setResourcesToDelete] = useState([]);
  const [newResourceTitle, setNewResourceTitle] = useState('');
  const [newResourceUrl, setNewResourceUrl] = useState('');

  const addQueuedResource = () => {
    const title = newResourceTitle.trim();
    const url = newResourceUrl.trim();
    if (!title || !url) { toast.error('Title and URL both required'); return; }
    if (!/^https?:\/\//i.test(url)) { toast.error('URL must start with http:// or https://'); return; }
    setLessonResources(prev => [...prev, { id: '_tmp_' + Date.now(), title, url, _pending: true }]);
    setNewResourceTitle('');
    setNewResourceUrl('');
  };

  // Upload a PDF (or image) directly — server saves it, returns a URL, we
  // treat that URL like any other resource link.
  const [isUploadingResource, setIsUploadingResource] = useState(false);
  const handleResourceFileUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setIsUploadingResource(true);
    const toastId = toast.loading(`Uploading ${file.name}…`);
    try {
      const res = await uploadApi.uploadFiles([file]);
      const uploaded = res.data.files?.[0];
      if (!uploaded?.fileUrl) throw new Error('No fileUrl returned');
      // Build absolute URL so student's browser can hit it directly
      const absoluteUrl = window.location.origin + uploaded.fileUrl;
      const defaultTitle = uploaded.originalName?.replace(/\.[^/.]+$/, '') || 'Resource';
      setLessonResources(prev => [...prev, {
        id: '_tmp_' + Date.now(),
        title: newResourceTitle.trim() || defaultTitle,
        url: absoluteUrl,
        _pending: true
      }]);
      setNewResourceTitle('');
      setNewResourceUrl('');
      toast.success(`${file.name} added — Save the lesson to publish it`, { id: toastId });
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed', { id: toastId });
    } finally {
      setIsUploadingResource(false);
    }
  };

  const removeQueuedResource = (id) => {
    const r = lessonResources.find(x => x.id === id);
    if (r && !r._pending) {
      // existing server-side row — mark for deletion on save
      setResourcesToDelete(prev => [...prev, id]);
    }
    setLessonResources(prev => prev.filter(x => x.id !== id));
  };

  const handleSaveLesson = async () => {
    if (!lessonForm.title.trim()) { toast.error('Title required'); return; }
    if (lessonTarget === 'technique' && !lessonTechniqueSlug) {
      toast.error('Pick a technique to attach this video to'); return;
    }
    try {
      let lessonId;
      // Send exactly one of {course_id, technique_slug}; server rejects both/neither.
      const targetFields = lessonTarget === 'technique'
        ? { technique_slug: lessonTechniqueSlug, course_id: null }
        : { course_id: lessonCourseId, technique_slug: null };

      if (editingLesson) {
        await lmsApi.updateLesson(editingLesson.id, { ...lessonForm, ...targetFields });
        lessonId = editingLesson.id;
      } else {
        const res = await lmsApi.createLesson({ ...lessonForm, ...targetFields });
        lessonId = res.data.id;
      }
      // Sync resources — post new ones, delete removed ones
      for (const r of lessonResources.filter(r => r._pending)) {
        try { await lmsApi.addLessonResource(lessonId, { title: r.title, url: r.url }); }
        catch (_) { /* keep going even if one fails */ }
      }
      for (const id of resourcesToDelete) {
        try { await lmsApi.deleteLessonResource(id); } catch (_) {}
      }
      toast.success(editingLesson ? 'Lesson updated' : 'Lesson created');
      setShowLessonModal(false);
      setEditingLesson(null);
      setLessonResources([]);
      setResourcesToDelete([]);
      setNewResourceTitle('');
      setNewResourceUrl('');
      loadCourses();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save lesson');
    }
  };

  const handleDeleteLesson = async (id) => {
    if (!confirm('Delete this lesson?')) return;
    try {
      await lmsApi.deleteLesson(id);
      toast.success('Lesson deleted');
      loadCourses();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const openNewLesson = (courseId) => {
    setLessonCourseId(courseId);
    setEditingLesson(null);
    setLessonTarget('course');            // default: attach to this course
    setLessonTechniqueSlug('');
    // Inherit levels from parent course by default — admin can override in Advanced.
    const parent = courses.find(c => c.id === courseId);
    const inheritedLevels = parent?.levels?.length ? parent.levels : ['L1', 'L2'];
    setLessonForm({ title: '', description: '', youtube_url: '', duration_seconds: 0, levels: inheritedLevels, is_published: true, sort_order: 0 });
    setLessonResources([]);
    setResourcesToDelete([]);
    setNewResourceTitle('');
    setNewResourceUrl('');
    setShowLessonModal(true);
  };

  const openEditLesson = async (lesson) => {
    setEditingLesson(lesson);
    // Prime the target radio from the lesson's existing attachment
    setLessonTarget(lesson.technique_slug ? 'technique' : 'course');
    setLessonTechniqueSlug(lesson.technique_slug || '');
    setLessonCourseId(lesson.course_id || null);
    setLessonForm({
      title: lesson.title,
      description: lesson.description || '',
      youtube_url: lesson.youtube_url || '',
      duration_seconds: lesson.duration_seconds || 0,
      levels: lesson.levels || ['L1', 'L2'],
      is_published: lesson.is_published,
      sort_order: lesson.sort_order || 0
    });
    setResourcesToDelete([]);
    setNewResourceTitle('');
    setNewResourceUrl('');
    // Load existing resources for this lesson
    try {
      const res = await lmsApi.getLessonResources(lesson.id);
      setLessonResources((res.data || []).map(r => ({ ...r, _pending: false })));
    } catch (_) {
      setLessonResources([]);
    }
    setShowLessonModal(true);
  };

  const toggleLevel = (formSetter, current, level) => {
    if (current.includes(level)) {
      if (current.length > 1) formSetter(prev => ({ ...prev, levels: current.filter(l => l !== level) }));
    } else {
      formSetter(prev => ({ ...prev, levels: [...current, level] }));
    }
  };

  if (isLoading) return <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={openNewCourse} className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          <Plus className="w-4 h-4 mr-2" /> New Course
        </button>
      </div>

      {courses.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No courses yet. Create your first course.</p>
      ) : (
        <div className="space-y-3">
          {courses.map((course, courseIndex) => (
            <div key={course.id} className="bg-white border border-gray-200 rounded-lg">
              <div
                className="flex items-center justify-between p-4 cursor-pointer"
                onClick={() => setExpandedCourse(expandedCourse === course.id ? null : course.id)}
              >
                <div className="flex items-center space-x-3">
                  {expandedCourse === course.id ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  <div>
                    <h3 className="font-medium text-gray-900">{course.title}</h3>
                    <div className="flex items-center space-x-2 mt-0.5">
                      {course.levels?.map(l => (
                        <span key={l} className={`text-xs px-1.5 py-0.5 rounded ${l === 'L1' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>{l}</span>
                      ))}
                      <span className="text-xs text-gray-400">{course.lesson_count} lessons</span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${course.is_published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {course.is_published ? 'Published' : 'Draft'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center space-x-1" onClick={e => e.stopPropagation()}>
                  <button onClick={() => moveCourse(course, courseIndex, 'up')} disabled={courseIndex === 0} className="p-1 text-gray-400 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed" title="Move up"><ChevronUp className="w-4 h-4" /></button>
                  <button onClick={() => moveCourse(course, courseIndex, 'down')} disabled={courseIndex === courses.length - 1} className="p-1 text-gray-400 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed" title="Move down"><ChevronDown className="w-4 h-4" /></button>
                  <button onClick={() => openEditCourse(course)} className="p-1.5 text-gray-400 hover:text-primary-600" title="Edit"><Edit2 className="w-4 h-4" /></button>
                  <button onClick={() => handleDeleteCourse(course.id)} className="p-1.5 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>

              {expandedCourse === course.id && (
                <div className="border-t border-gray-100 p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-medium text-gray-700">Lessons</h4>
                    <button onClick={() => openNewLesson(course.id)} className="flex items-center text-sm text-primary-600 hover:text-primary-700">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Lesson
                    </button>
                  </div>
                  {course.lessons?.length > 0 ? (
                    <div className="space-y-2">
                      {course.lessons.map((lesson, i) => (
                        <div key={lesson.id} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-100">
                          <div className="flex items-center space-x-3">
                            <span className="text-xs text-gray-400 w-6">{i + 1}.</span>
                            <div>
                              <p className="text-sm font-medium text-gray-800">{lesson.title}</p>
                              <div className="flex items-center space-x-2 mt-0.5">
                                {lesson.youtube_video_id && <span className="text-xs text-gray-400">Video</span>}
                                {lesson.levels?.map(l => (
                                  <span key={l} className={`text-xs px-1 py-0 rounded ${l === 'L1' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>{l}</span>
                                ))}
                                <span className={`text-xs ${lesson.is_published ? 'text-green-600' : 'text-yellow-600'}`}>
                                  {lesson.is_published ? 'Published' : 'Draft'}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-0.5">
                            <button onClick={() => moveLesson(course.lessons, lesson, i, 'up')} disabled={i === 0} className="p-1 text-gray-400 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed" title="Move up"><ChevronUp className="w-3.5 h-3.5" /></button>
                            <button onClick={() => moveLesson(course.lessons, lesson, i, 'down')} disabled={i === course.lessons.length - 1} className="p-1 text-gray-400 hover:text-primary-600 disabled:opacity-30 disabled:cursor-not-allowed" title="Move down"><ChevronDown className="w-3.5 h-3.5" /></button>
                            <button onClick={() => navigate(`/lms/lessons/${lesson.id}`)} className="p-1 text-gray-400 hover:text-primary-600" title="Preview"><Eye className="w-3.5 h-3.5" /></button>
                            <button onClick={() => openEditLesson(lesson)} className="p-1 text-gray-400 hover:text-primary-600" title="Edit lesson"><Edit2 className="w-3.5 h-3.5" /></button>
                            <button onClick={() => handleDeleteLesson(lesson.id)} className="p-1 text-gray-400 hover:text-red-600" title="Delete lesson"><Trash2 className="w-3.5 h-3.5" /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-400 text-center py-4">No lessons yet</p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Course Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingCourse ? 'Edit Course' : 'New Course'}</h3>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g. Coding Fundamentals" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="One-line description shown to students" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Available to</label>
                <div className="flex space-x-3">
                  {['L1', 'L2'].map(l => (
                    <label key={l} className="flex items-center space-x-1.5 cursor-pointer">
                      <input type="checkbox" checked={form.levels.includes(l)} onChange={() => toggleLevel(setForm, form.levels, l)} />
                      <span className="text-sm">{l === 'L1' ? 'L1 (trial)' : 'L2 (paid)'}</span>
                    </label>
                  ))}
                </div>
              </div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" checked={form.is_published} onChange={e => setForm({ ...form, is_published: e.target.checked })} />
                <span className="text-sm font-medium text-gray-700">Published (visible to students)</span>
              </label>

              {/* Advanced options — collapsed by default */}
              <details className="mt-1">
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 select-none">Advanced (optional)</summary>
                <div className="mt-2 pl-2 border-l-2 border-gray-100">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Thumbnail URL</label>
                  <input type="text" value={form.thumbnail_url} onChange={e => setForm({ ...form, thumbnail_url: e.target.value })} className="w-full px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm" placeholder="https://... (leave empty for auto-placeholder)" />
                  <p className="text-[10px] text-gray-400 mt-1">A card image for the course. If empty, a colored placeholder shows.</p>
                </div>
              </details>
            </div>
            <div className="flex space-x-2 mt-4">
              <button onClick={handleSaveCourse} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Lesson Modal */}
      {showLessonModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingLesson ? 'Edit Lesson' : 'New Lesson'}</h3>
              <button onClick={() => setShowLessonModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input type="text" value={lessonForm.title} onChange={e => setLessonForm({ ...lessonForm, title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="e.g. Introduction to Peg System" />
              </div>
              {/* Where to display — the video lives EITHER under a course OR inside a technique's chat page. */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <label className="block text-xs font-medium text-gray-600 mb-2">Where to display this video</label>
                <div className="flex gap-4 mb-2">
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="lesson-target" value="course" checked={lessonTarget === 'course'} onChange={() => setLessonTarget('course')} />
                    <span>Inside a course</span>
                  </label>
                  <label className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="lesson-target" value="technique" checked={lessonTarget === 'technique'} onChange={() => setLessonTarget('technique')} />
                    <span>Inside a technique</span>
                  </label>
                </div>
                {lessonTarget === 'course' ? (
                  <select
                    value={lessonCourseId || ''}
                    onChange={e => setLessonCourseId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="">Pick a course…</option>
                    {courses.map(c => (
                      <option key={c.id} value={c.id}>{c.title}</option>
                    ))}
                  </select>
                ) : (
                  <select
                    value={lessonTechniqueSlug}
                    onChange={e => setLessonTechniqueSlug(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                  >
                    <option value="">Pick a technique…</option>
                    {techniques.map(t => (
                      <option key={t.slug} value={t.slug}>{t.name}</option>
                    ))}
                  </select>
                )}
                <p className="text-[10px] text-gray-500 mt-1.5">
                  {lessonTarget === 'course'
                    ? 'Shows under the course in the Courses tab.'
                    : 'Shows as a "Related Video" on the technique\'s chat page (not in Courses).'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">YouTube URL</label>
                <input type="text" value={lessonForm.youtube_url} onChange={e => setLessonForm({ ...lessonForm, youtube_url: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="https://youtube.com/watch?v=..." />
                <p className="text-[10px] text-gray-400 mt-1">Duration is auto-detected from YouTube — you don't need to type it.</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={lessonForm.description} onChange={e => setLessonForm({ ...lessonForm, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Short text shown under the video" />
              </div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input type="checkbox" checked={lessonForm.is_published} onChange={e => setLessonForm({ ...lessonForm, is_published: e.target.checked })} />
                <span className="text-sm font-medium text-gray-700">Published (visible to students)</span>
              </label>

              {/* Advanced options — hidden by default. Levels inherit from parent course. */}
              <details>
                <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700 select-none">Advanced (optional)</summary>
                <div className="mt-2 pl-2 border-l-2 border-gray-100">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Available to</label>
                  <div className="flex space-x-3">
                    {['L1', 'L2'].map(l => (
                      <label key={l} className="flex items-center space-x-1.5 cursor-pointer">
                        <input type="checkbox" checked={lessonForm.levels.includes(l)} onChange={() => toggleLevel(setLessonForm, lessonForm.levels, l)} />
                        <span className="text-sm">{l === 'L1' ? 'L1 (trial)' : 'L2 (paid)'}</span>
                      </label>
                    ))}
                  </div>
                  <p className="text-[10px] text-gray-400 mt-1">Defaults to the course's levels. Override only if this lesson has different access rules.</p>
                </div>
              </details>

              {/* Downloadable resources — inline, saved together with the lesson */}
              <div className="border-t border-gray-200 pt-3 mt-2">
                <div className="flex items-center mb-2">
                  <Upload className="w-4 h-4 mr-1.5 text-gray-500" />
                  <h4 className="text-sm font-medium text-gray-700">Downloadable Resources</h4>
                  {lessonResources.length > 0 && (
                    <span className="ml-2 text-xs text-gray-400">({lessonResources.length})</span>
                  )}
                </div>
                <p className="text-[11px] text-gray-500 mb-2">
                  Google Drive / Dropbox / any share link. Students click to download.
                </p>

                {/* Existing resources list */}
                {lessonResources.length > 0 && (
                  <div className="space-y-1.5 mb-3">
                    {lessonResources.map(r => (
                      <div key={r.id} className="flex items-center gap-2 p-2 bg-gray-50 rounded text-xs">
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-800 truncate">{r.title}</div>
                          <a href={r.url} target="_blank" rel="noreferrer noopener" className="text-[10px] text-primary-600 truncate block hover:underline" title={r.url}>{r.url}</a>
                        </div>
                        {r._pending && <span className="text-[9px] font-mono uppercase text-amber-600">new</span>}
                        <button type="button" onClick={() => removeQueuedResource(r.id)} className="text-gray-400 hover:text-red-600 flex-shrink-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Add-resource form — two ways: paste URL OR upload PDF/image directly */}
                <div className="grid grid-cols-1 gap-2">
                  <input
                    type="text"
                    value={newResourceTitle}
                    onChange={e => setNewResourceTitle(e.target.value)}
                    placeholder="Title (optional — auto-generated from filename when uploading)"
                    className="w-full px-2.5 py-1.5 border border-gray-300 rounded text-sm"
                  />

                  {/* Option 1: Upload a file directly */}
                  <label className={`flex items-center justify-center gap-1.5 px-3 py-2 border-2 border-dashed border-gray-300 rounded text-xs font-medium cursor-pointer hover:border-primary-400 hover:bg-primary-50 ${isUploadingResource ? 'opacity-50 pointer-events-none' : ''}`}>
                    <Upload className="w-4 h-4 text-primary-600" />
                    <span className="text-gray-700">{isUploadingResource ? 'Uploading…' : 'Upload PDF or image'}</span>
                    <input
                      type="file"
                      accept="application/pdf,image/*"
                      onChange={handleResourceFileUpload}
                      className="hidden"
                    />
                  </label>

                  {/* Option 2: OR paste an external URL */}
                  <div className="flex items-center gap-2 text-[10px] text-gray-400">
                    <span className="flex-1 border-t border-gray-200"></span>
                    <span>or paste a link</span>
                    <span className="flex-1 border-t border-gray-200"></span>
                  </div>
                  <div className="flex gap-2">
                    <input
                      type="url"
                      value={newResourceUrl}
                      onChange={e => setNewResourceUrl(e.target.value)}
                      placeholder="https://drive.google.com/…"
                      className="flex-1 px-2.5 py-1.5 border border-gray-300 rounded text-sm"
                    />
                    <button
                      type="button"
                      onClick={addQueuedResource}
                      className="flex items-center px-3 py-1.5 bg-gray-700 text-white rounded text-xs font-medium hover:bg-gray-800"
                    >
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add
                    </button>
                  </div>
                </div>
                {(lessonResources.some(r => r._pending) || resourcesToDelete.length > 0) && (
                  <p className="text-[10px] text-amber-600 mt-2">
                    ⚠ Unsaved changes to resources — click Save below to apply.
                  </p>
                )}
              </div>
            </div>
            <div className="flex space-x-2 mt-4">
              <button onClick={handleSaveLesson} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Save</button>
              <button onClick={() => setShowLessonModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function toggleLevel(formSetter, current, level) {
  if (current.includes(level)) {
    if (current.length > 1) formSetter(prev => ({ ...prev, levels: current.filter(l => l !== level) }));
  } else {
    formSetter(prev => ({ ...prev, levels: [...current, level] }));
  }
}

// ========================
// ASSESSMENTS TAB
// ========================
function AssessmentsTab() {
  const [assessments, setAssessments] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [lessons, setLessons] = useState([]);   // {id, title, course_title} — flat list across all courses
  const emptyForm = { title: '', description: '', lesson_id: '', assessment_type: 'quiz', time_limit_minutes: '', passing_score: 0, max_attempts: 1, levels: ['L1', 'L2'], is_published: false, questions: [] };
  const [form, setForm] = useState(emptyForm);
  const navigate = useNavigate();

  useEffect(() => { loadAssessments(); loadLessons(); }, []);

  const loadAssessments = async () => {
    try {
      const res = await assessmentApi.getAssessments();
      setAssessments(res.data);
    } catch (error) {
      toast.error('Failed to load assessments');
    } finally { setIsLoading(false); }
  };

  // Flatten every lesson across every course into one array for the dropdown.
  const loadLessons = async () => {
    try {
      const coursesRes = await lmsApi.getCourses();
      const courses = coursesRes.data || [];
      const flat = [];
      for (const c of courses) {
        try {
          const detail = await lmsApi.getCourse(c.id);
          for (const l of (detail.data.lessons || [])) {
            flat.push({ id: l.id, title: l.title, course_title: c.title });
          }
        } catch (_) { /* skip courses we can't read */ }
      }
      setLessons(flat);
    } catch (_) { /* silent — dropdown just becomes empty */ }
  };

  // Open the modal in EDIT mode with existing data pre-filled.
  const openEditModal = async (id) => {
    try {
      const res = await assessmentApi.getAssessment(id);
      const a = res.data;
      setForm({
        title: a.title || '',
        description: a.description || '',
        lesson_id: a.lesson_id || '',
        assessment_type: a.assessment_type || 'quiz',
        time_limit_minutes: a.time_limit_minutes || '',
        passing_score: a.passing_score || 0,
        max_attempts: a.max_attempts || 1,
        levels: a.levels && a.levels.length ? a.levels : ['L1', 'L2'],
        is_published: !!a.is_published,
        questions: [], // question edits go via dedicated endpoints — see the note in the modal
      });
      setEditingId(id);
      setShowModal(true);
    } catch (_) {
      toast.error('Failed to load assessment');
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  // AI extraction — admin picks a questions file + optional answer key file,
  // then clicks Extract. Gemini reads both and returns parsed MCQs with the
  // correct answers taken straight from the key sheet.
  const [isExtracting, setIsExtracting] = useState(false);
  const [questionsFile, setQuestionsFile] = useState(null);
  const [answerKeyFile, setAnswerKeyFile] = useState(null);

  const runExtraction = async () => {
    if (!questionsFile) {
      toast.error('Pick the questions file first');
      return;
    }
    setIsExtracting(true);
    const label = answerKeyFile
      ? 'Reading questions + answer key (30-60s)…'
      : 'Reading questions (30-60s)…';
    const toastId = toast.loading(label);
    try {
      const res = await assessmentApi.extractQuestionsFromFile(questionsFile, answerKeyFile);
      const extracted = res.data.questions || [];
      if (extracted.length === 0) {
        toast.error('No questions found in that file', { id: toastId });
        return;
      }
      setForm(prev => ({ ...prev, questions: [...prev.questions, ...extracted] }));
      const msg = res.data.used_answer_key
        ? `Added ${extracted.length} questions — answers from your key sheet`
        : `Added ${extracted.length} questions — answers AI-inferred (upload a key sheet next time for accuracy)`;
      toast.success(msg, { id: toastId });
      setQuestionsFile(null);
      setAnswerKeyFile(null);
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Extraction failed';
      toast.error(errorMsg, { id: toastId });
    } finally {
      setIsExtracting(false);
    }
  };

  const addQuestion = () => {
    setForm(prev => ({
      ...prev,
      questions: [...prev.questions, {
        question_type: 'mcq',
        question_text: '',
        options: [{ key: 'A', text: '' }, { key: 'B', text: '' }, { key: 'C', text: '' }, { key: 'D', text: '' }],
        correct_answer: '',
        points: 1,
        explanation: ''
      }]
    }));
  };

  const updateQuestion = (index, field, value) => {
    setForm(prev => {
      const questions = [...prev.questions];
      questions[index] = { ...questions[index], [field]: value };
      return { ...prev, questions };
    });
  };

  const updateOption = (qIndex, optIndex, text) => {
    setForm(prev => {
      const questions = [...prev.questions];
      const options = [...questions[qIndex].options];
      options[optIndex] = { ...options[optIndex], text };
      questions[qIndex] = { ...questions[qIndex], options };
      return { ...prev, questions };
    });
  };

  const removeQuestion = (index) => {
    setForm(prev => ({ ...prev, questions: prev.questions.filter((_, i) => i !== index) }));
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    const payload = {
      ...form,
      lesson_id: form.lesson_id || null,
      time_limit_minutes: form.time_limit_minutes ? parseInt(form.time_limit_minutes) : null,
    };
    try {
      if (editingId) {
        await assessmentApi.updateAssessment(editingId, payload);
        toast.success('Assessment updated');
      } else {
        await assessmentApi.createAssessment(payload);
        toast.success('Assessment created');
      }
      closeModal();
      loadAssessments();
    } catch (error) {
      toast.error(editingId ? 'Failed to update assessment' : 'Failed to create assessment');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Delete this assessment?')) return;
    try {
      await assessmentApi.deleteAssessment(id);
      toast.success('Assessment deleted');
      loadAssessments();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (isLoading) return <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowModal(true)} className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          <Plus className="w-4 h-4 mr-2" /> New Assessment
        </button>
      </div>

      {assessments.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No assessments yet.</p>
      ) : (
        <div className="space-y-2">
          {assessments.map(a => (
            <div key={a.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">{a.title}</h3>
                <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                  <span className="capitalize">{a.assessment_type}</span>
                  <span>{a.question_count} questions</span>
                  {a.levels?.map(l => (
                    <span key={l} className={`px-1.5 py-0.5 rounded ${l === 'L1' ? 'bg-green-50 text-green-600' : 'bg-blue-50 text-blue-600'}`}>{l}</span>
                  ))}
                  <span className={a.is_published ? 'text-green-600' : 'text-yellow-600'}>
                    {a.is_published ? 'Published' : 'Draft'}
                  </span>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <button onClick={() => navigate(`/lms/assessments/${a.id}`)} className="p-1.5 text-gray-400 hover:text-primary-600" title="Preview"><Eye className="w-4 h-4" /></button>
                <button onClick={() => openEditModal(a.id)} className="p-1.5 text-gray-400 hover:text-primary-600" title="Edit"><Edit2 className="w-4 h-4" /></button>
                <button onClick={() => handleDelete(a.id)} className="p-1.5 text-gray-400 hover:text-red-600" title="Delete"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Assessment Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">{editingId ? 'Edit Assessment' : 'New Assessment'}</h3>
              <button onClick={closeModal}><X className="w-5 h-5 text-gray-400" /></button>
            </div>

            <div className="space-y-3">
              <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Assessment title" />
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Description" />

              {/* Optionally attach to a specific lesson — students can ALSO find
                   any assessment via the standalone LMS → Assessments menu, so this
                   is now optional. Attach only if you want the quiz to appear inside
                   a specific lesson's Assessment tab. */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Attach to Lesson (optional)</label>
                <select
                  value={form.lesson_id}
                  onChange={e => setForm({ ...form, lesson_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white"
                >
                  <option value="">— Standalone (visible in Assessments menu) —</option>
                  {lessons.map(l => (
                    <option key={l.id} value={l.id}>{l.course_title} → {l.title}</option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Students always find published quizzes in <strong>LMS → Assessments</strong>. Attach to a lesson only if you want the quiz to ALSO show up inside that specific lesson.
                </p>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Type</label>
                  <select value={form.assessment_type} onChange={e => setForm({ ...form, assessment_type: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="quiz">Quiz</option>
                    <option value="assignment">Assignment</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Time Limit (min)</label>
                  <input type="number" value={form.time_limit_minutes} onChange={e => setForm({ ...form, time_limit_minutes: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="No limit" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Max Attempts</label>
                  <input type="number" value={form.max_attempts} onChange={e => setForm({ ...form, max_attempts: parseInt(e.target.value) || 1 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>

              <div className="flex items-center space-x-4">
                <div className="flex space-x-2">
                  {['L1', 'L2'].map(l => (
                    <label key={l} className="flex items-center space-x-1">
                      <input type="checkbox" checked={form.levels.includes(l)} onChange={() => toggleLevel(setForm, form.levels, l)} />
                      <span className="text-sm">{l}</span>
                    </label>
                  ))}
                </div>
                <label className="flex items-center space-x-1">
                  <input type="checkbox" checked={form.is_published} onChange={e => setForm({ ...form, is_published: e.target.checked })} />
                  <span className="text-sm">Published</span>
                </label>
              </div>

              {/* Questions — only editable on create. In edit mode, questions
                   are managed elsewhere so we don't wipe/reinsert them accidentally. */}
              <div className="border-t border-gray-200 pt-3 mt-3">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-medium text-gray-700">Questions ({form.questions.length})</h4>
                  {!editingId && (
                    <button onClick={addQuestion} className="text-sm text-primary-600 hover:text-primary-700 flex items-center">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Manually
                    </button>
                  )}
                </div>

                {/* AI extraction panel — upload question paper + answer key, get parsed MCQs */}
                {!editingId && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-3">
                    <div className="flex items-center mb-2">
                      <Sparkles className="w-4 h-4 mr-1.5 text-purple-600" />
                      <h5 className="text-sm font-semibold text-purple-900">Bulk import from PDF / image</h5>
                    </div>
                    <p className="text-[11px] text-purple-800 mb-3">
                      Upload the question paper. Optionally upload the official answer key too — AI will use it for exact correct answers instead of guessing.
                    </p>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
                      {/* Questions file */}
                      <label className="block">
                        <span className="text-[11px] font-medium text-purple-900 block mb-1">
                          1. Question paper <span className="text-red-500">*</span>
                        </span>
                        <div className="flex items-center gap-2 text-xs">
                          <input
                            type="file"
                            accept="application/pdf,image/*"
                            onChange={(e) => setQuestionsFile(e.target.files?.[0] || null)}
                            className="flex-1 text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-white file:text-purple-700 file:cursor-pointer border border-purple-200 rounded bg-white"
                          />
                        </div>
                        {questionsFile && (
                          <span className="text-[10px] text-purple-700 mt-1 block truncate" title={questionsFile.name}>
                            ✓ {questionsFile.name}
                          </span>
                        )}
                      </label>

                      {/* Answer key file (optional) */}
                      <label className="block">
                        <span className="text-[11px] font-medium text-purple-900 block mb-1">
                          2. Answer key <span className="text-purple-500">(optional but recommended)</span>
                        </span>
                        <div className="flex items-center gap-2 text-xs">
                          <input
                            type="file"
                            accept="application/pdf,image/*"
                            onChange={(e) => setAnswerKeyFile(e.target.files?.[0] || null)}
                            className="flex-1 text-xs file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-white file:text-purple-700 file:cursor-pointer border border-purple-200 rounded bg-white"
                          />
                        </div>
                        {answerKeyFile && (
                          <span className="text-[10px] text-purple-700 mt-1 block truncate" title={answerKeyFile.name}>
                            ✓ {answerKeyFile.name}
                          </span>
                        )}
                      </label>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={runExtraction}
                        disabled={!questionsFile || isExtracting}
                        className="flex items-center px-3 py-1.5 bg-purple-600 text-white text-xs font-medium rounded hover:bg-purple-700 disabled:opacity-50"
                      >
                        <Sparkles className="w-3.5 h-3.5 mr-1" />
                        {isExtracting ? 'Extracting…' : 'Extract Questions'}
                      </button>
                      {(questionsFile || answerKeyFile) && !isExtracting && (
                        <button
                          type="button"
                          onClick={() => { setQuestionsFile(null); setAnswerKeyFile(null); }}
                          className="text-xs text-purple-600 hover:text-purple-800"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {editingId && (
                  <p className="text-xs text-gray-500 italic mb-2">
                    Question editing coming soon. To modify questions on an existing assessment, delete it and recreate.
                  </p>
                )}

                {form.questions.map((q, i) => (
                  <div key={i} className="bg-gray-50 rounded-lg p-3 mb-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-gray-500">Q{i + 1}</span>
                      <div className="flex items-center space-x-2">
                        <select value={q.question_type} onChange={e => updateQuestion(i, 'question_type', e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1">
                          <option value="mcq">MCQ</option>
                          <option value="subjective">Subjective</option>
                        </select>
                        <input type="number" value={q.points} onChange={e => updateQuestion(i, 'points', parseFloat(e.target.value) || 1)} className="w-16 text-xs border border-gray-300 rounded px-2 py-1" placeholder="Points" />
                        <button onClick={() => removeQuestion(i)} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>

                    <textarea value={q.question_text} onChange={e => updateQuestion(i, 'question_text', e.target.value)} rows={2} className="w-full px-2 py-1.5 border border-gray-300 rounded text-sm mb-2" placeholder="Question text" />

                    {q.question_type === 'mcq' && (
                      <div className="space-y-1.5">
                        {q.options.map((opt, oi) => (
                          <div key={oi} className="flex items-center space-x-2">
                            <input
                              type="radio"
                              name={`correct_${i}`}
                              checked={q.correct_answer === opt.key}
                              onChange={() => updateQuestion(i, 'correct_answer', opt.key)}
                            />
                            <span className="text-xs font-medium text-gray-500 w-4">{opt.key}.</span>
                            <input
                              type="text"
                              value={opt.text}
                              onChange={e => updateOption(i, oi, e.target.value)}
                              className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                              placeholder={`Option ${opt.key}`}
                            />
                          </div>
                        ))}
                      </div>
                    )}

                    <input type="text" value={q.explanation || ''} onChange={e => updateQuestion(i, 'explanation', e.target.value)} className="w-full px-2 py-1 border border-gray-200 rounded text-xs mt-2" placeholder="Explanation (optional)" />
                  </div>
                ))}
              </div>
            </div>

            <div className="flex space-x-2 mt-4">
              <button onClick={handleSave} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">{editingId ? 'Save Changes' : 'Create Assessment'}</button>
              <button onClick={closeModal} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========================
// LIVE SESSIONS TAB
// ========================
function LiveSessionsTab() {
  const [sessions, setSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', meeting_url: '', meeting_platform: 'zoom', scheduled_at: '', duration_minutes: 60, levels: ['L1', 'L2'] });

  useEffect(() => { loadSessions(); }, []);

  const loadSessions = async () => {
    try {
      const res = await lmsApi.getLiveSessions();
      setSessions(res.data);
    } catch (error) {
      toast.error('Failed to load sessions');
    } finally { setIsLoading(false); }
  };

  const handleSave = async () => {
    if (!form.title || !form.meeting_url || !form.scheduled_at) {
      toast.error('Title, meeting URL, and time are required');
      return;
    }
    try {
      await lmsApi.createLiveSession(form);
      toast.success('Live session created');
      setShowModal(false);
      setForm({ title: '', description: '', meeting_url: '', meeting_platform: 'zoom', scheduled_at: '', duration_minutes: 60, levels: ['L1', 'L2'] });
      loadSessions();
    } catch (error) {
      toast.error('Failed to create session');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Cancel this session?')) return;
    try {
      await lmsApi.deleteLiveSession(id);
      toast.success('Session cancelled');
      loadSessions();
    } catch (error) {
      toast.error('Failed to cancel');
    }
  };

  if (isLoading) return <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>;

  return (
    <div>
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowModal(true)} className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
          <Plus className="w-4 h-4 mr-2" /> New Session
        </button>
      </div>

      {sessions.length === 0 ? (
        <p className="text-center text-gray-500 py-8">No live sessions yet.</p>
      ) : (
        <div className="space-y-2">
          {sessions.map(s => (
            <div key={s.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
              <div>
                <h3 className="font-medium text-gray-900">{s.title}</h3>
                <div className="flex items-center space-x-2 mt-1 text-xs text-gray-500">
                  <span>{new Date(s.scheduled_at).toLocaleString()}</span>
                  <span>{s.duration_minutes} min</span>
                  <span className="capitalize">{s.meeting_platform.replace('_', ' ')}</span>
                  <span className={`px-1.5 py-0.5 rounded ${
                    s.status === 'scheduled' ? 'bg-blue-100 text-blue-700' :
                    s.status === 'completed' ? 'bg-green-100 text-green-700' :
                    s.status === 'cancelled' ? 'bg-red-100 text-red-700' :
                    'bg-yellow-100 text-yellow-700'
                  }`}>{s.status}</span>
                </div>
              </div>
              {s.status === 'scheduled' && (
                <button onClick={() => handleDelete(s.id)} className="p-1.5 text-gray-400 hover:text-red-600">
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">New Live Session</h3>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <input type="text" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Session title" />
              <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Description" />
              <input type="text" value={form.meeting_url} onChange={e => setForm({ ...form, meeting_url: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg" placeholder="Meeting URL (Zoom/Meet link)" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Platform</label>
                  <select value={form.meeting_platform} onChange={e => setForm({ ...form, meeting_platform: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                    <option value="zoom">Zoom</option>
                    <option value="google_meet">Google Meet</option>
                    <option value="teams">Teams</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Duration (min)</label>
                  <input type="number" value={form.duration_minutes} onChange={e => setForm({ ...form, duration_minutes: parseInt(e.target.value) || 60 })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Scheduled Date & Time</label>
                <input type="datetime-local" value={form.scheduled_at} onChange={e => setForm({ ...form, scheduled_at: e.target.value })} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <div className="flex space-x-2">
                {['L1', 'L2'].map(l => (
                  <label key={l} className="flex items-center space-x-1">
                    <input type="checkbox" checked={form.levels.includes(l)} onChange={() => toggleLevel(setForm, form.levels, l)} />
                    <span className="text-sm">{l}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex space-x-2 mt-4">
              <button onClick={handleSave} className="flex-1 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">Create Session</button>
              <button onClick={() => setShowModal(false)} className="px-4 py-2 border border-gray-300 rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ========================
// SUBMISSIONS TAB
// ========================
function SubmissionsTab() {
  const navigate = useNavigate();
  return (
    <div className="text-center py-8">
      <Upload className="w-12 h-12 mx-auto text-gray-300 mb-3" />
      <p className="text-gray-600 mb-4">Review and grade student submissions</p>
      <button
        onClick={() => navigate('/lms/submissions')}
        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
      >
        Open Submissions
      </button>
    </div>
  );
}

export default ManageLMS;
